import { IFormsSpecificForm, IFormsGenericForm } from "@cms/iprox";
import parse from "html-react-parser";
import {
  IFormContentDataViewModel,
  IFormFieldContentViewModel,
  IFormSectionsViewModel,
  parsedHtmlElement
} from "../interface/formContentData";

type IFormFields = (IFormsSpecificForm | IFormsGenericForm)["formFields"];

export class FormContentResponseMapper {
  static map(response: IFormsSpecificForm | IFormsGenericForm): IFormContentDataViewModel {
    return {
      formName: response.alias ?? "unexpected_form_name",
      language: response.formContent.formLanguage,
      title: response.title,
      introduction: this.isSpecificFormResponse(response)
        ? this.parseWhenDefined(response.formContent.formIntroduction)
        : undefined,
      privacyText: this.isGenericFormResponse(response)
        ? this.parseWhenDefined(response.formContent.formOutro)
        : undefined,
      downloadInformation: this.isGenericFormResponse(response) 
        ? this.parseWhenDefined(response.formContent.formDownloadInformation) 
        : undefined,
      formSections: this.isSpecificFormResponse(response)
        ? this.mapFormSections(response.formSections)
        : undefined,
      formFieldContent: this.mapFormFieldContent(response),
      thankYouContent: this.isSpecificFormResponse(response)
        ? this.parseWhenDefined(response.formContent.formThankYou)
        : undefined
    };
  }

  static isSpecificFormResponse(
    response: IFormsSpecificForm | IFormsGenericForm
  ): response is IFormsSpecificForm {
    return response.formContent.hasOwnProperty("formIntroduction");
  }

  static isGenericFormResponse(
    response: IFormsSpecificForm | IFormsGenericForm
  ): response is IFormsGenericForm {
    return response.formContent.hasOwnProperty("formOutro");
  }

  static mapFormSections(formSections: IFormsSpecificForm["formSections"]): IFormSectionsViewModel[] {
    return formSections.formSection.map((formSection) => ({
      sectionName: formSection.sectionIdentifier,
      sectionTitle: formSection.sectionTitle ?? "",
      sectionIntroduction: formSection.sectionText ?? undefined
    }));
  }

  static mapFormFieldContent(response: IFormsSpecificForm | IFormsGenericForm): IFormFieldContentViewModel[] {
    type IFormFieldContent = IFormFields["formFieldContent"][0];

    return response.formFields.formFieldContent.map((formFieldContent: IFormFieldContent) => ({
      fieldName: formFieldContent.inputIdentifier,
      label: formFieldContent.inputLabel ?? "",
      additionalInformation: formFieldContent.inputAdditionalInformation ?? undefined,
      formFieldOptions: this.isSpecificFormResponse(response)
        ? this.mapFormFieldOptions(formFieldContent.inputIdentifier, response.formFieldOptions)
        : undefined
    }));
  }

  static mapFormFieldOptions(
    inputIdentifier: string,
    formFieldOptions: IFormsSpecificForm["formFieldOptions"]
  ): IFormFieldContentViewModel["formFieldOptions"] {
    const formOptionsMatchingIdentifier = formFieldOptions?.formFieldOptions?.filter(
      (option) => option.inputIdentifier == inputIdentifier
    );
    if (!formOptionsMatchingIdentifier || formOptionsMatchingIdentifier.length === 0) {
      return undefined;
    }

    return formOptionsMatchingIdentifier.map((formFieldOption) => ({
      label: formFieldOption.dropdownOptionLabel ?? "",
      text: formFieldOption.dropdownOptionText ?? "",
      value: formFieldOption.dropdownOptionValue ?? ""
    }));
  }

  private static parseWhenDefined(content?: string | null | undefined): parsedHtmlElement | undefined {
    if (content === null || content === undefined) return undefined;
    return parse(content);
  }
}
